import { useState, useEffect, useCallback } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { CalendarDays, Save, Trash2, Plus, Check } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const MONTHS = [
  "", "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
];

function formatCurrency(amount) {
  if (amount === undefined || amount === null) return "0.00";
  return new Intl.NumberFormat("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(Math.abs(amount));
}

export default function MonthlyClosings() {
  const [closings, setClosings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editMode, setEditMode] = useState(null); // {year, month}
  const [editEntries, setEditEntries] = useState([]);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);

  const fetchClosings = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/monthly-closings`);
      setClosings(response.data);
    } catch (err) {
      toast.error("Failed to load closings");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchClosings();
  }, [fetchClosings]);

  const startEdit = async (year, month) => {
    try {
      const response = await axios.get(`${API}/monthly-closings/${year}/${month}`);
      setEditMode({ year, month });
      setEditEntries(response.data.entries || []);
    } catch (err) {
      toast.error("Failed to load closing data");
    }
  };

  const handleSave = async () => {
    if (!editMode) return;
    try {
      await axios.post(`${API}/monthly-closings`, {
        year: editMode.year,
        month: editMode.month,
        entries: editEntries,
      });
      toast.success(`Closing saved for ${MONTHS[editMode.month]} ${editMode.year}`);
      setEditMode(null);
      fetchClosings();
    } catch (err) {
      toast.error("Failed to save closing");
    }
  };

  const handleDelete = async (year, month) => {
    try {
      await axios.delete(`${API}/monthly-closings/${year}/${month}`);
      toast.success("Closing deleted");
      fetchClosings();
    } catch (err) {
      toast.error("Failed to delete closing");
    }
  };

  const updateEntry = (index, value) => {
    const updated = [...editEntries];
    updated[index] = { ...updated[index], closing: parseFloat(value) || 0 };
    setEditEntries(updated);
  };

  const currentYear = new Date().getFullYear();
  const yearOptions = [];
  for (let y = currentYear; y >= currentYear - 5; y--) yearOptions.push(y);

  if (editMode) {
    return (
      <div data-testid="monthly-closings-edit">
        <div className="mb-6 flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">
              Edit Closing: {MONTHS[editMode.month]} {editMode.year}
            </h1>
            <p className="text-sm text-slate-500 mt-1">
              Set closing balances for each account
            </p>
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setEditMode(null)}
              data-testid="closing-cancel-btn"
            >
              Cancel
            </Button>
            <Button
              size="sm"
              onClick={handleSave}
              data-testid="closing-save-btn"
            >
              <Save className="w-4 h-4 mr-1" /> Save Closing
            </Button>
          </div>
        </div>

        <Card className="border border-slate-200 shadow-none">
          <CardContent className="p-0">
            <div className="max-h-[600px] overflow-auto">
              <Table data-testid="closing-edit-table">
                <TableHeader>
                  <TableRow className="bg-slate-50 sticky top-0">
                    <TableHead className="font-semibold">Account</TableHead>
                    <TableHead className="text-right font-semibold w-48">Closing Balance</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {editEntries.map((entry, idx) => (
                    <TableRow key={entry.name || `entry-${idx}`}>
                      <TableCell className="text-sm">{entry.name}</TableCell>
                      <TableCell className="text-right">
                        <input
                          type="number"
                          step="0.01"
                          value={entry.closing}
                          onChange={(e) => updateEntry(idx, e.target.value)}
                          className="w-40 px-2 py-1 border border-slate-300 rounded-sm text-sm text-right font-mono focus:outline-none focus:ring-1 focus:ring-slate-400"
                          data-testid={`closing-input-${idx}`}
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                  {editEntries.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={2} className="text-center text-slate-500 py-8">
                        No accounts found. Upload a Trial Balance first.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div data-testid="monthly-closings">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-slate-900" data-testid="closings-title">
          Monthly Closings
        </h1>
        <p className="text-sm text-slate-500 mt-1">
          Manually define closing balances for each month
        </p>
      </div>

      <Card className="border border-slate-200 shadow-none mb-6">
        <CardContent className="p-4">
          <div className="flex items-center gap-4 flex-wrap">
            <div className="flex items-center gap-2">
              <CalendarDays className="w-4 h-4 text-slate-400" />
              <select
                value={selectedYear}
                onChange={(e) => setSelectedYear(parseInt(e.target.value))}
                className="px-2 py-1 border border-slate-300 rounded-sm text-sm"
                data-testid="closing-year-select"
              >
                {yearOptions.map((y) => (
                  <option key={y} value={y}>{y}</option>
                ))}
              </select>
              <select
                value={selectedMonth}
                onChange={(e) => setSelectedMonth(parseInt(e.target.value))}
                className="px-2 py-1 border border-slate-300 rounded-sm text-sm"
                data-testid="closing-month-select"
              >
                {MONTHS.slice(1).map((m, i) => (
                  <option key={m} value={i + 1}>{m}</option>
                ))}
              </select>
            </div>
            <Button
              size="sm"
              onClick={() => startEdit(selectedYear, selectedMonth)}
              data-testid="closing-add-btn"
            >
              <Plus className="w-4 h-4 mr-1" /> Add/Edit Closing
            </Button>
          </div>
        </CardContent>
      </Card>

      {loading ? (
        <div className="flex items-center justify-center h-32">
          <div className="spinner"></div>
        </div>
      ) : (
        <Card className="border border-slate-200 shadow-none">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold text-slate-700 flex items-center gap-2">
              <Check className="w-4 h-4" />
              Saved Monthly Closings
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <Table data-testid="closings-list-table">
              <TableHeader>
                <TableRow className="bg-slate-50">
                  <TableHead className="font-semibold">Period</TableHead>
                  <TableHead className="text-center font-semibold w-24">Accounts</TableHead>
                  <TableHead className="text-center font-semibold w-32">Last Updated</TableHead>
                  <TableHead className="text-right font-semibold w-32">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {closings.map((c) => (
                  <TableRow key={c.label}>
                    <TableCell className="font-medium">{c.label}</TableCell>
                    <TableCell className="text-center font-mono text-sm">
                      {c.entries?.length || 0}
                    </TableCell>
                    <TableCell className="text-center text-sm text-slate-500">
                      {c.updated_at ? new Date(c.updated_at).toLocaleDateString() : "-"}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => startEdit(c.year, c.month)}
                          className="text-xs h-7"
                          data-testid={`closing-edit-${c.year}-${c.month}`}
                        >
                          Edit
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleDelete(c.year, c.month)}
                          className="text-xs h-7 text-red-600 border-red-200 hover:bg-red-50"
                          data-testid={`closing-delete-${c.year}-${c.month}`}
                        >
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
                {closings.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={4} className="text-center text-slate-500 py-8">
                      No monthly closings defined yet. Use the selector above to add one.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
